import i18n from '@/i18n'
import {
	type Status,
	ProjectInitialized,
	InitializingInProgress,
	ImportInProgress,
	ImportCompleted,
	ExportCompleted,
	ExportInProgress,
	TaskUnsuccessful,
	AdminRightsAreMissing,
	NotInitialized,
	NoDomainConfigForInstance,
	ConfigurationMissing,
	DeleteCompleted,
	ConfirmDelete,
	DeleteInProgress,
	RefreshNeeded,
} from '@/models/jira-enums-generated'
import {
	DueDateApproaching,
	ConditionallyImplemented,
	DueDateCritical,
	HighPriority,
	Implemented,
	LowPriority,
	MediumPriority,
	NeedsAttention,
	NotImplemented,
	DueDateOnTime,
	DueDateOverdue,
	PartiallyImplemented,
	RuleConditionallyImplemented,
	RuleImplemented,
	RuleNeedsAttention,
	RuleNotImplemented,
	RuleWillNotApply,
	RuleWillNotImplement,
	WillNotApply,
	WillNotImplement,
	type Permission,
	type RuleStatus,
	type AuditType,
	BasicAudit,
	ForeignInternalAudit,
	InternalAudit,
	Confidentiality,
	Integrity,
	Availability,
	RT_Unscored,
	RT_Preserve,
	RT_Share,
	RT_Reduce,
	RT_Avoid,
	RT_Exclude,
} from '@/models/enums-generated'
import type { Instance, ModuleMeta, Responsible } from '@/models/api-generated'
import { enUS, et } from 'date-fns/locale'
import { Process, System, type ModuleType } from '@/models/module-enums-generated'
import type { MethodForm, ModuleForm, RuleForm } from '@/types/module'
import { computed } from 'vue'

export function formatLocale() {
	switch (i18n.global.locale.value) {
		case 'et':
			return et
		case 'en':
		default:
			return enUS
	}
}

export function getDateFormat(): string {
	return i18n.global.locale.value === 'en' ? 'MM/dd/yyyy' : 'dd.MM.yyyy'
}

export function getMethodStatusText(status: string | undefined) {
	switch (status) {
		case PartiallyImplemented:
			return i18n.global.t('status.partiallyImplemented')
		case Implemented:
			return i18n.global.t('status.implemented')
		case WillNotApply:
			return i18n.global.t('status.willNotApply')
		case NeedsAttention:
			return i18n.global.t('status.needsAttention')
		case NotImplemented:
			return i18n.global.t('status.notImplemented')
		case WillNotImplement:
			return i18n.global.t('status.willNotImplement')
		case ConditionallyImplemented:
			return i18n.global.t('status.conditionallyImplemented')
		default:
			return ''
	}
}

export function getRuleStatusFromMethod(status?: string) {
	switch (status) {
		case Implemented:
			return RuleImplemented
		case WillNotApply:
			return RuleWillNotApply
		case NeedsAttention:
			return RuleNeedsAttention
		case NotImplemented:
			return RuleNotImplemented
		case WillNotImplement:
			return RuleWillNotImplement
		default:
			return 0
	}
}

export function getModuleTypeText(type: ModuleType) {
	if (type === System) {
		return i18n.global.t('module.systemModule')
	} else if (type === Process) {
		return i18n.global.t('module.processModule')
	}

	return ''
}

export function getModuleStatusText(status: string) {
	return i18n.global.t(`module.status.${status}`)
}

export function getRuleStatusText(status: number | undefined) {
	switch (status) {
		case 0:
			return i18n.global.t('status.notImplemented')
		case 1:
			return i18n.global.t('status.implemented')
		case 2:
			return i18n.global.t('status.willNotImplement')
		case 3:
			return i18n.global.t('status.needsAttention')
		case 4:
			return i18n.global.t('status.willNotApply')
		case 5:
			return i18n.global.t('status.conditionallyImplemented')
		default:
			return ''
	}
}

export function getMethodStatusForProgress(status: string | undefined) {
	switch (status) {
		case 'i':
			return 'Implemented'
		case 'pi':
			return 'Partially implemented'
		case 'ni':
			return 'Not implemented'
		case 'wna':
			return 'Will not apply'
		case 'na':
			return 'Needs attention'
		case 'wni':
			return 'Will not implement'
		case 'ci':
			return 'Conditionally implemented'
	}
	return ''
}

export const isCommentRequired = (status: RuleStatus, comment: string) =>
	[RuleWillNotApply, RuleWillNotImplement].includes(status) && !comment

export const calculateParentRuleStatus = (subruleStatuses: RuleStatus[]) => {
	const count = {
		implemented: 0,
		partiallyImplemented: 0,
		notImplemented: 0,
		willNotImplement: 0,
		willNotApply: 0,
		needsAttention: 0,
	}

	subruleStatuses.forEach((status) => {
		switch (status) {
			case RuleImplemented:
				count.implemented++
				break
			case RuleNotImplemented:
				count.notImplemented++
				break
			case RuleWillNotApply:
				count.willNotApply++
				break
			case RuleNeedsAttention:
				count.needsAttention++
				break
			case RuleWillNotImplement:
				count.willNotImplement++
				break
		}
	})

	const subrulesLength = subruleStatuses.length

	let status

	switch (true) {
		case count.implemented == subrulesLength:
			status = RuleImplemented
			break
		case count.needsAttention > 0:
			status = RuleNeedsAttention
			break
		case count.implemented > 0 && count.implemented + count.willNotApply + count.willNotImplement == subrulesLength:
			status = RuleConditionallyImplemented
			break
		case count.willNotImplement > 0 && count.willNotImplement + count.willNotApply == subrulesLength:
			status = RuleWillNotImplement
			break
		case count.willNotApply == subrulesLength:
			status = RuleWillNotApply
			break
	}

	return status
}

export function formatDate(iso: Date | string) {
	return new Date(iso).toLocaleDateString(i18n.global.locale.value, {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	})
}

export function formatDateTime(iso: string) {
	return new Date(iso).toLocaleString(i18n.global.locale.value, {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	})
}

export const jiraResponseTextMap: { [key in Status]?: string } = {
	[InitializingInProgress]: 'jira.projectIsInitializing',
	[ImportInProgress]: 'jira.importProgress',
	[ImportCompleted]: 'jira.importCompleted',
	[TaskUnsuccessful]: 'jira.isAnotherError',
	[ExportCompleted]: 'jira.exportCompleted',
	[ExportInProgress]: 'jira.exportingIssues',
	[DeleteInProgress]: 'jira.deleteInProgress',
	[DeleteCompleted]: 'jira.deleteCompleted',
	[ConfirmDelete]: 'jira.confirmIssueDelete',
	[NoDomainConfigForInstance]: 'jira.status.noDomainConfig',
	[RefreshNeeded]: 'jira.importCompleted',
}

export function getTranslatedJiraResponseText(status: Status | undefined): string {
	const translationKey = jiraResponseTextMap[status!]
	return translationKey ? i18n.global.t(translationKey) : i18n.global.t('jira.status.taskUnsuccessful')
}

export const jiraStatusMap: { [key in Status]?: string } = {
	[NotInitialized]: 'jira.status.notInitialized',
	[AdminRightsAreMissing]: 'jira.status.adminRightsMissing',
	[ConfigurationMissing]: 'jira.status.informationMissing',
	[NoDomainConfigForInstance]: 'jira.status.noDomainConfig',
	[TaskUnsuccessful]: 'jira.status.taskUnsuccessful',
	[ProjectInitialized]: 'jira.status.isInitialized',
}

export function getTranslatedJiraStatus(status: Status | undefined): string {
	const translationKey = jiraStatusMap[status!]
	return translationKey ? i18n.global.t(translationKey) : i18n.global.t('jira.status.taskUnsuccessful')
}

export function getAuditText(audit: boolean | undefined) {
	switch (audit) {
		case true:
			return i18n.global.t('audit.active')
		case false:
			return i18n.global.t('audit.finishedAudits')
		default:
			return ''
	}
}

export function getPriorityText(priority: string | undefined) {
	switch (priority) {
		case undefined:
			return i18n.global.t('misc.nothing')
		case LowPriority:
			return i18n.global.t('low')
		case MediumPriority:
			return i18n.global.t('medium')
		case HighPriority:
			return i18n.global.t('high')
		default:
			return i18n.global.t('misc.none')
	}
}

export function getTypeText(type: string | undefined) {
	if (!type) return ''
	return i18n.global.t(type)
}

export function getDueDateStatusText(status: string | undefined) {
	switch (status) {
		case DueDateOnTime:
			return i18n.global.t('date.onTime')
		case DueDateApproaching:
			return i18n.global.t('date.approaching')
		case DueDateCritical:
			return i18n.global.t('date.critical')
		case DueDateOverdue:
			return i18n.global.t('date.overdue')
		default:
			return ''
	}
}

export function getActiveText(active: number) {
	return active ? i18n.global.t('active') : i18n.global.t('notActive')
}

export function generateDueDateParams(dueDateStatus: string) {
	const formatDate = (days: number) => {
		const date = new Date()
		date.setDate(date.getDate() + days)
		return date.toISOString().split('T')[0]
	}

	const ranges: Record<string, string[]> = {
		[DueDateOnTime]: [DueDateOnTime, formatDate(5)],
		[DueDateApproaching]: [formatDate(3), formatDate(5)],
		[DueDateCritical]: [formatDate(0), formatDate(2)],
		[DueDateOverdue]: [DueDateOverdue],
	}

	const params = new URLSearchParams()
	ranges[dueDateStatus]?.forEach((date) => params.append('dueDate', date))
	return params
}

export function daysUntilDate(targetDate: Date): number | null {
	if (!targetDate) return null
	const date = new Date(targetDate)

	if (isNaN(date.getTime())) return null

	const today = new Date()
	today.setHours(0, 0, 0, 0)
	date.setHours(0, 0, 0, 0)
	const differenceInTime = date.getTime() - today.getTime()
	return Math.ceil(differenceInTime / (1000 * 3600 * 24))
}

export function genRouteID(id: number, name: string, component: string): string {
	const routeObj = { id, name, component }
	return btoa(JSON.stringify(routeObj))
}

const statusIconMap: Record<string, { iconClass: string; showAsterisk?: boolean }> = {
	[NotImplemented]: { iconClass: 'bi bi-circle-fill me-2 ms-2 pie3' },
	[PartiallyImplemented]: { iconClass: 'bi bi-circle-fill me-2 ms-2 pie2' },
	[Implemented]: { iconClass: 'bi bi-circle-fill me-2 ms-2 pie1' },
	[ConditionallyImplemented]: { iconClass: 'bi bi-exclamation-circle-fill me-2 ms-2 pie1' },
	[WillNotApply]: { iconClass: 'bi bi-circle-fill me-2 ms-2 pie4' },
	[WillNotImplement]: { iconClass: 'bi bi-circle-fill me-2 ms-2 pie4' },
	[NeedsAttention]: { iconClass: 'bi bi-circle-fill pie6 me-2 ms-2', showAsterisk: true },
}

export function getStatusIcon(statusName: string, isRenewed: boolean) {
	const { iconClass = 'bi bi-question-circle' } = statusIconMap[statusName] || {}
	const title = getMethodStatusText(statusName)
	return {
		iconClass,
		showAsterisk: statusName === NeedsAttention && isRenewed,
		title,
	}
}

export const priorityIconMap: Record<string, string[]> = {
	[HighPriority]: ['bi', 'bi-chevron-double-up', 'pie3'],
	[MediumPriority]: ['bi', 'bi-grip-horizontal', 'pie2'],
	[LowPriority]: ['bi', 'bi-chevron-double-down', 'pie1'],
}

export function getConnectionIcon(moduleType: string, assetName?: string) {
	let title = i18n.global.t('allBusinessProcesses')
	let badgeText = 'BP'

	if (moduleType !== 'P' && assetName) {
		title = assetName
		badgeText = 'A'
	}

	return {
		title,
		badgeText,
	}
}

const typeIconMap: Record<string, string[]> = {
	main: ['bi bi-shield btn-xs'],
	standard: ['bi bi-shield-shaded btn-xs'],
	high: ['bi bi-shield-fill btn-xs'],
}

export function getTypeIcon(type: string) {
	return typeIconMap[type] || []
}

export function hasPermission(...permissions: Permission[]) {
	return !!sessionMap.user.roles.find((x) => x.permissions.find((y: Permission) => permissions.includes(y)))
}

export function hasRole(...roleIDs: number[]) {
	return !!sessionMap.user.roles.find((x) => roleIDs.includes(x.id))
}

export function hasPermissionOwn(responsible: Responsible | undefined, all: Permission, own: Permission) {
	if (hasPermission(all)) {
		return true
	}

	if (hasPermission(own)) {
		if (responsible?.type === 'users') {
			return sessionMap.user.id == responsible.id
		} else if (responsible?.type === 'roles') {
			return sessionMap.user.roles.some((x) => x.id === responsible.id)
		}
	}

	return false
}

export function getResponsibleIcon(responsible?: Responsible) {
	if (responsible?.user?.active === 0) {
		return 'bi-person-fill me-2 pie3'
	} else if (responsible?.user?.active === 1) {
		return 'bi-person-fill me-2 pie1'
	} else if (responsible?.role?.id) {
		return 'bi-people-fill me-2'
	} else {
		return 'bi-people-fill me-2 pie4'
	}
}

export function getServiceProviderIcon(responsible?: Responsible) {
	if (responsible?.user?.active === 0) {
		return 'bi-person-badge-fill me-2 pie3'
	} else if (responsible?.user?.active === 1) {
		return 'bi-person-badge-fill me-2 pie1'
	} else if (responsible?.role?.id) {
		return 'bi-person-badge-fill me-2'
	} else {
		return 'bi-person-badge-fill me-2 pie4'
	}
}

export function auditTypeStr(typ: AuditType) {
	switch (typ) {
		case BasicAudit:
			return i18n.global.t('audit.basicAudit')
		case ForeignInternalAudit:
			return i18n.global.t('audit.foreignInternalAudit')
		case InternalAudit:
			return i18n.global.t('audit.internalAudit')
		default:
			return i18n.global.t('audit.noAudit')
	}
}

export function newModuleForm(): ModuleForm {
	return {
		name: '',
		description: '',
		version: 'custom',
		type: '',
		isCustom: true,
		active: true,
		instance: defaultInstance(),
		isApplicable: false,
		assetAssociations: [],
		urlReferences: [],
		documentReferences: [],
	}
}

export function newRuleForm(): RuleForm {
	return { value: '' }
}

export function newMethodForm(): MethodForm {
	return {
		name: '',
		description: '',
		type: '',
		securityNeed: '',
		confidentiality: false,
		integrity: false,
		availability: false,
		rules: [newRuleForm()],
		isExpanded: false,
		status: {
			id: 0,
			force: false,
		},
		applicable: false,
	}
}

export function newModuleMeta(): ModuleMeta {
	return {
		id: 0,
		module: {
			id: 0,
			description: '',
			name: '',
			type: '',
			metadata: [],
			methods: [],
			version: '',
			isCustom: false,
		},
		instance: {
			id: 0,
			name: '',
			description: '',
			address: '',
			email: '',
			phone: '',
			contact: '',
			createdBy: 0,
			mainInstance: 0,
		},
		numAssets: 0,
		assetAssociations: [],
		IsOverwritten: false,
		urlReferences: [],
		documentReferences: [],
		active: false,
		isApplicable: false,
	}
}

export function truncate(val?: string, len = 18) {
	return val ? (val.length > len ? val.slice(0, len) + '...' : val) : '-'
}

export function getInfoSecCoreText(type: string | undefined) {
	switch (type) {
		case Confidentiality:
			return i18n.global.t('confidentiality')
		case Integrity:
			return i18n.global.t('integrity')
		case Availability:
			return i18n.global.t('availability')
		default:
			return ''
	}
}

export const shallowCloneArray = <T extends object>(arr: T[]): T[] => arr.map((x) => ({ ...x }))

export function getTreatmentText(type: string | undefined) {
	switch (type) {
		case RT_Unscored:
			return i18n.global.t('risk.treatment.unscored')
		case RT_Preserve:
			return i18n.global.t('risk.treatment.preserve')
		case RT_Share:
			return i18n.global.t('risk.treatment.share')
		case RT_Reduce:
			return i18n.global.t('risk.treatment.reduce')
		case RT_Avoid:
			return i18n.global.t('risk.treatment.avoid')
		case RT_Exclude:
			return i18n.global.t('risk.treatment.exclude')
		default:
			return ''
	}
}

export function formatBytes(bytes: number, decimals = 2) {
	if (!+bytes) return '0 B'

	const k = 1024
	const dm = decimals < 0 ? 0 : decimals
	const sizes = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']

	const i = Math.floor(Math.log(bytes) / Math.log(k))

	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export function isRowClick(e: MouseEvent) {
	let el = e.target as HTMLElement | null | undefined

	if (el?.classList?.contains('bi-plus-square') || el?.classList?.contains('bi-dash-square')) return false

	let depth = 0
	do {
		if (++depth >= 2) return false
		el = el?.parentElement
	} while (el?.tagName !== 'TR')

	return true
}

export function averageResidual(risks: { residual: number }[] = []) {
	if (!risks.length) return 0
	const sum = risks.reduce((acc, r) => acc + (r.residual ?? 0), 0)
	return sum / risks.length
}

export const viewing = computed(() => (sessionMap.viewing?.length === 1 ? sessionMap.viewing[0] : 0))

export function toAbsoluteUrl(raw: string) {
	const value = raw?.trim()
	if (!value) return ''

	const hasScheme = /^[a-zA-Z][\w+.-]*:/.test(value)
	const protocolRelative = value.startsWith('//')
	const appRelative = value.startsWith('/')

	if (hasScheme) return new URL(value).toString()
	if (protocolRelative) return new URL(`https:${value}`).toString()
	if (appRelative) return new URL(value, window.location.origin).toString()
	return new URL(`https://${value}`).toString()
}
export function defaultInstance(): Instance {
	return {
		id: viewing.value,
		name: '',
		description: '',
		address: '',
		email: '',
		phone: '',
		contact: '',
		createdBy: 0,
		mainInstance: 0,
	}
}
